import type Player from 'video.js/dist/types/player';
import { useEffect, useRef } from 'react';
import stylex from '@stylexjs/stylex';
import videojs from 'video.js';

import type { IContainerProps } from '~/components/utils/Container';

import 'video.js/dist/video-js.css';

import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import { useTranslation } from 'react-i18next';

export type IHlsVideoPlayerProps = IContainerProps & {
  src: string;
  type?: string;
  onReady?: (player: Player) => void;
  controls?: boolean;
  fluid?: boolean;
  width?: number;
  height?: number;
  playButton?: boolean;
  preload?: string;
  language?: string;
};

const styles = stylex.create({
  host: {
    flexShrink: 0,
    borderRadius: shapeTokens.corner$xs,
    overflow: 'hidden',
  },
});

export const HlsVideoPlayer: React.FC<IHlsVideoPlayerProps> = (props) => {
  const {
    sx,
    src,
    type = 'application/x-mpegURL',
    onReady,
    controls,
    fluid,
    width,
    height,
    playButton,
    preload = 'metadata',
    language,
  } = props;

  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const options = {
      width,
      height,
      controls,
      fluid,
      bigPlayButton: !!playButton,
      preload,
      sources: [{ src, type }],
      language: language ?? i18n.language,
    };

    // Make sure Video.js player is only initialized once.
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18
      // Strict Mode.
      const videoElement = document.createElement('video-js');

      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = videojs(videoElement, options, () => {
        onReady?.(player);
      }));

      // You could update an existing player in the `else` block here
      // on prop change, for example:
    } else {
      const player = playerRef.current;
      player.src(options.sources);
    }
  }, [
    src,
    type,
    videoRef,
    controls,
    fluid,
    onReady,
    width,
    height,
    playButton,
    preload,
    language,
    i18n,
  ]);

  // Dispose the Video.js player when the functional component unmounts.
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <div data-vjs-player {...stylex.props([styles.host, sx])}>
      <div ref={videoRef} />
    </div>
  );
};
